import {createContext, useContext} from 'react'

import type {SortDirection} from '../api/view/contracts'
import type {ColumnModel} from '../models/column-model'
import type {LocalSort} from './use-view-state-reducer/types'

export interface SortedByContext {
  /** The sorts for the current view in priority order (ie, 0 = primary sort, 1 = secondary sort ...) */
  sorts: ReadonlyArray<LocalSort>
  /** Change all sorts for the current view. */
  setSortedBy: (...sorts: ReadonlyArray<LocalSort>) => void
  /** Clear sorts for the current view. If index is passed, will clear sorts on and after that point. */
  clearSortedBy: (index?: number) => void
  /** `true` if there are unsaved sorting changes in the current view. */
  isSortedByDirty: boolean
  /** `true` if the current view is sorted. */
  isSorted: boolean
  /**
   * Set the sort direction for a column in the current view.
   * @param direction The direction to sort. If `null`, will clear the sort for this column
   * @param index The index of the sort (0 = primary, 1 = secondary, ...). All sorts after this index will be cleared.
   * Since a view cannot be sorted by the same column multiple times, it's possible that the resulting index will be
   * different from this index. For example, if the view is primary-sorted by Title, attempting to secondary-sort by
   * Title will just result in a view that is still only primary-sorted by Title.
   */
  setColumnSort: (column: ColumnModel, direction: SortDirection | null, index: number) => void
  /** Get the sort state for a column in the current view. */
  getColumnSort: (column: ColumnModel) => LocalSort | undefined
}

export const SortedByContext = createContext<SortedByContext | null>(null)

export const useSortedBy = () => {
  const ctx = useContext(SortedByContext)
  if (!ctx) throw new Error('useSortedBy must be used within a SortedByContext')
  return ctx
}
